import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'assessment_auto_advance_enabled';

/**
 * Hook to manage auto-advance functionality in assessments.
 * Dev-only feature that automatically advances to the next question after selection.
 * Toggle with Ctrl/Cmd+Shift+A keyboard shortcut.
 */
export function useAutoAdvance({ nextQuestion, currentQuestion, totalQuestions, loading }) {
  const [enabled, setEnabled] = useState(() => {
    if (process.env.NODE_ENV !== 'development') return false;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });

  // Track whether we should trigger advance after current render
  const shouldAdvanceRef = useRef(false);

  // Handle keyboard shortcut (Ctrl/Cmd+Shift+A)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setEnabled(prev => {
          const newValue = !prev;
          localStorage.setItem(STORAGE_KEY, String(newValue));
          return newValue;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Process auto-advance after response is set
  useEffect(() => {
    if (!shouldAdvanceRef.current) return;
    shouldAdvanceRef.current = false;

    // Don't advance if loading or on last question
    if (loading) return;
    if (currentQuestion >= totalQuestions) return;

    // Small delay to allow state updates to propagate
    const timer = setTimeout(() => {
      if (typeof nextQuestion === 'function') {
        nextQuestion();
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [loading, currentQuestion, totalQuestions, nextQuestion]);

  // Call this after handleResponse to trigger auto-advance
  const triggerAutoAdvance = useCallback(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (!enabled) return;
    shouldAdvanceRef.current = true;
  }, [enabled]);

  return { triggerAutoAdvance, enabled };
}
